import { CheckmarkOutline18Icon } from "@vygruppen/spor-icon-react";
import { AffectedTrain } from "features/CenterContent/RoleContent/Vaktleder/types";
import { groupTrainsByIncidentId } from "features/History/groupTrainSeries";
import { AffectedTrainFilter } from "features/VehicleSidebar/AffectedTrains/types";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterChip, HStack, Text, tokens } from "shared/components/Spor";
import { useTheme } from "styled-components";

function sanitizeFilterAgainstGroups(
  filter: AffectedTrainFilter,
  groupedTrains: Record<string, { trainSeries: Record<string, unknown> }>,
): AffectedTrainFilter {
  if (!filter) return null;

  const groupsContainIncidentFilter = Object.keys(groupedTrains).includes(
    filter.incidentId,
  );
  if (!groupsContainIncidentFilter) return null;

  const groupsInFilteredIncident =
    groupedTrains[filter.incidentId]?.trainSeries;
  const validSeriesInFilter = filter.trainSeriesIds.filter((seriesId) =>
    Object.keys(groupsInFilteredIncident ?? {}).includes(seriesId),
  );
  if (validSeriesInFilter.length === 0) return null;
  return { ...filter, trainSeriesIds: validSeriesInFilter };
}

function getIncidentFilterState(
  filter: AffectedTrainFilter,
  incidentId: string,
  allSeriesInIncident: string[],
) {
  if (!filter) return { filterHasIncident: false, includesAll: false };
  const filterHasIncident = filter?.incidentId === incidentId;
  const includesAll =
    filterHasIncident &&
    filter.trainSeriesIds.length === allSeriesInIncident.length &&
    filter.trainSeriesIds.every((seriesId) =>
      allSeriesInIncident.includes(seriesId),
    );

  return { filterHasIncident, includesAll };
}

function toggleAllSeriesInIncident(
  filter: AffectedTrainFilter,
  incidentId: string,
  allSeriesInIncident: string[],
): AffectedTrainFilter {
  const { includesAll } = getIncidentFilterState(
    filter,
    incidentId,
    allSeriesInIncident,
  );
  return includesAll
    ? null
    : { incidentId, trainSeriesIds: allSeriesInIncident };
}

function toggleSingleSeriesInIncident(
  filter: AffectedTrainFilter,
  incidentId: string,
  seriesId: string,
): AffectedTrainFilter {
  if (!filter) {
    return { incidentId, trainSeriesIds: [seriesId] };
  }
  const filterHasIncident = filter?.incidentId === incidentId;
  const seriesIsChecked =
    filterHasIncident && filter.trainSeriesIds.includes(seriesId);

  if (!filterHasIncident) {
    return { incidentId, trainSeriesIds: [seriesId] };
  }

  if (seriesIsChecked) {
    const isOnlyCheckedSeries = filter.trainSeriesIds.length === 1;
    if (isOnlyCheckedSeries) {
      return null;
    }
    return {
      incidentId,
      trainSeriesIds: filter.trainSeriesIds.filter((x) => x !== seriesId),
    };
  }

  return {
    incidentId,
    trainSeriesIds: [...filter.trainSeriesIds, seriesId],
  };
}

export type TrainFilterProps = {
  trains: AffectedTrain[];
  filter: AffectedTrainFilter;
  showFilter: boolean;
  setFilter: (filter: AffectedTrainFilter) => void;
  selectedTrains?: AffectedTrain[];
  batchUpdatedSelectedTrains?: (affectedTrains: AffectedTrain[]) => void;
};

export const TrainFilter: FC<TrainFilterProps> = ({
  trains,
  filter,
  showFilter,
  setFilter,
  selectedTrains: _selectedTrains,
  batchUpdatedSelectedTrains: _batchUpdatedSelectedTrains,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const groupedTrains = useMemo(
    () => groupTrainsByIncidentId(trains),
    [trains],
  );

  // when train groups change, check if the current filter is still valid
  useEffect(() => {
    if (!filter) return;
    const sanitized = sanitizeFilterAgainstGroups(filter, groupedTrains);
    if (JSON.stringify(sanitized) === JSON.stringify(filter)) return;
    setFilter(sanitized);
  }, [groupedTrains, filter, setFilter]);

  return showFilter &&
    groupedTrains &&
    Object.keys(groupedTrains).length > 0 ? (
    <HStack
      wrap="wrap"
      backgroundColor={theme.colorBackgroundMain}
      px="12px"
      borderBottom={`1px solid ${theme.colorOutlineSecondary}`}
      overflowY="auto"
      maxHeight="38%"
    >
      {Object.entries(groupedTrains).map(([incident, group], i, groups) => {
        const allSeriesInIncident = Object.keys(group.trainSeries);
        const { includesAll } = getIncidentFilterState(
          filter,
          incident,
          allSeriesInIncident,
        );
        return (
          <HStack
            w="100%"
            key={incident}
            gap="6px"
            wrap="wrap"
            py="12px"
            borderBottom={
              i !== groups.length - 1
                ? `1px solid ${theme.colorOutlineSecondary}`
                : undefined
            }
          >
            <HStack width="100%" mb={tokens.default.size.spacing[1]}>
              <Text variant="xs">{group.stretchName}</Text>
              <FilterChip
                size="xs"
                ml="auto"
                mr={0}
                checked={includesAll}
                onCheckedChange={() =>
                  setFilter(
                    toggleAllSeriesInIncident(
                      filter,
                      incident,
                      allSeriesInIncident,
                    ),
                  )
                }
              >
                {t("Marker alle")}
              </FilterChip>
            </HStack>
            {Object.entries(group.trainSeries).map(([seriesId]) => {
              const seriesIsChecked =
                filter?.incidentId === incident &&
                filter?.trainSeriesIds.includes(seriesId);

              return (
                <FilterChip
                  size="xs"
                  key={`${incident}_${seriesId}`}
                  checked={seriesIsChecked}
                  onCheckedChange={() =>
                    setFilter(
                      toggleSingleSeriesInIncident(filter, incident, seriesId),
                    )
                  }
                >
                  {seriesIsChecked ? <CheckmarkOutline18Icon /> : null}

                  {seriesId}
                </FilterChip>
              );
            })}
          </HStack>
        );
      })}
    </HStack>
  ) : null;
};

export type { AffectedTrainFilter };
